// Groups react-dropzone accepted files by their top-level folder name, which we
// treat as the matCode. Files dropped without an enclosing folder (single-segment
// path) have no matCode and are returned separately as `looseFiles`.
//
// react-dropzone attaches a `path` to each File when a directory is dropped, e.g.
// "MAT001/img1.jpg" or "/MAT001/sub/img1.jpg". Nested subfolders are flattened:
// the matCode is always the first segment and the S3 key later uses only file.name.

export interface FileWithPath extends File {
  path?: string;
}

export interface MatCodeGroup {
  matCode: string;
  files: FileWithPath[];
}

export interface GroupByMatCodeResult {
  groups: MatCodeGroup[];
  looseFiles: FileWithPath[];
}

/** Returns the path react-dropzone assigned, falling back to the file name. */
function getPath(file: FileWithPath): string {
  return (file.path ?? file.name).replace(/^\/+/, "");
}

/** matCode = first path segment when the file lives inside a folder, else null. */
export function matCodeFromPath(file: FileWithPath): string | null {
  const segments = getPath(file).split("/");
  if (segments.length < 2) return null; // no enclosing folder
  const matCode = segments[0].trim();
  return matCode.length > 0 ? matCode : null;
}

export function groupByMatCode(files: FileWithPath[]): GroupByMatCodeResult {
  const groupMap = new Map<string, FileWithPath[]>();
  const looseFiles: FileWithPath[] = [];

  for (const file of files) {
    const matCode = matCodeFromPath(file);
    if (matCode === null) {
      looseFiles.push(file);
      continue;
    }
    const bucket = groupMap.get(matCode);
    if (bucket) {
      bucket.push(file);
    } else {
      groupMap.set(matCode, [file]);
    }
  }

  const groups: MatCodeGroup[] = Array.from(groupMap.entries()).map(
    ([matCode, groupedFiles]) => ({ matCode, files: groupedFiles })
  );

  return { groups, looseFiles };
}
